import {
  akkumuleretVed,
  forloeb,
  krydsning,
  STANDARD_HORISONT_AAR,
  type Forloeb,
  type Profil,
  type Satser,
} from "@biloplader/calc";
import { productToPv, type Beregning } from "../../lib/api";

/** Number of chart series slots (--serie-1 … --serie-4). */
export const MAX_VALGTE = 4;

/** One agreement plotted over time. */
export interface Serie {
  produktversionId: number;
  navn: string;
  /** Chart colour slot 0-3 — follows the entity, so deselecting one never repaints the others. */
  slot: number;
  farve: string;
  forloeb: Forloeb;
  bindingsperiodeMdr: number;
  beregning: Beregning;
}

/** Where two agreements swap places, phrased for a reader. */
export interface Vending {
  /** The one that is cheapest before the crossing. */
  foer: Serie;
  /** The one that is cheapest after it. */
  efter: Serie;
  tidAar: number;
  belob: number;
}

export interface Projektion {
  aar: number;
  serier: Serie[];
  /** Every break-even inside the horizon, earliest first. */
  vendinger: Vending[];
  /**
   * The one break-even worth telling the reader about: where the agreement that
   * ends up cheapest takes the lead for good. Marking every crossing turns the
   * chart into a thicket of rings, and the earliest one is often between two
   * runners-up — neither is the decision the reader is making.
   */
  noegleVending: Vending | null;
  /** Largest accumulated amount plotted — the y-axis has to reach it. */
  maxBelob: number;
  /** Cheapest agreement at the end of the horizon. */
  billigstTilSidst: Serie | null;
}

/** Accumulated cost at an arbitrary point — re-exported so the chart can hover-sample. */
export { akkumuleretVed };

/**
 * Build the comparison model for the selected agreements.
 *
 * Colour slots come from `slotAf` (owned by the results list) rather than from
 * the ordering here, so a series keeps its colour when the selection changes.
 */
export function byggProjektion(
  valgte: Beregning[],
  profil: Profil,
  satser: Satser,
  slotAf: (produktversionId: number) => number,
  aar: number = STANDARD_HORISONT_AAR,
): Projektion {
  const serier: Serie[] = valgte.map((b) => {
    const slot = slotAf(b.produkt.produktversionId);
    return {
      produktversionId: b.produkt.produktversionId,
      navn: [b.produkt.udbyder, b.produkt.produkt].filter(Boolean).join(" "),
      slot,
      farve: `var(--serie-${slot + 1})`,
      forloeb: forloeb(profil, productToPv(b.produkt), satser, aar),
      bindingsperiodeMdr: b.produkt.bindingsperiodeMdr,
      beregning: b,
    };
  });

  const vendinger: Vending[] = [];
  for (let i = 0; i < serier.length; i++) {
    for (let j = i + 1; j < serier.length; j++) {
      const a = serier[i];
      const b = serier[j];
      const k = krydsning(a.forloeb, b.forloeb, aar);
      if (!k) continue;
      // Whoever is lower just before the crossing is the "before" one.
      const foerA = akkumuleretVed(a.forloeb, k.tidAar / 2) < akkumuleretVed(b.forloeb, k.tidAar / 2);
      vendinger.push({
        foer: foerA ? a : b,
        efter: foerA ? b : a,
        tidAar: k.tidAar,
        belob: k.belob,
      });
    }
  }
  vendinger.sort((x, y) => x.tidAar - y.tidAar);

  const maxBelob = serier.reduce((m, s) => Math.max(m, akkumuleretVed(s.forloeb, aar)), 0);
  const billigstTilSidst =
    serier.length === 0
      ? null
      : serier.reduce((best, s) =>
          akkumuleretVed(s.forloeb, aar) < akkumuleretVed(best.forloeb, aar) ? s : best,
        );

  // Curves are straight after year 0, so once the eventual winner has passed
  // everyone else it stays in front — the last crossing it wins is the moment
  // the decision settles.
  const vundne = vendinger.filter((v) => v.efter.produktversionId === billigstTilSidst?.produktversionId);
  const noegleVending = vundne.length > 0 ? vundne[vundne.length - 1] : null;

  return { aar, serier, vendinger, noegleVending, maxBelob, billigstTilSidst };
}

/**
 * Nice round y-axis ticks covering 0…max — 4-6 steps on a 1/2/5 × 10ⁿ ladder.
 * Returns the tick values plus the axis top, which is the last tick.
 */
export function yTicks(max: number): { ticks: number[]; top: number } {
  if (max <= 0) return { ticks: [0], top: 1 };
  const raw = max / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => raw <= s) ?? 10 * magnitude;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(Math.round(v));
  return { ticks, top };
}

/** "4,0 år" / "7 måneder" — a break-even reads better in months when it is early. */
export function formatTid(
  tidAar: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const mdr = Math.round(tidAar * 12);
  if (mdr < 24) return t("sam.mdr", { n: mdr });
  return t("sam.aar", { n: tidAar.toFixed(1).replace(".", ",") });
}
